import React from "react";
import Badge from "../../../components/Badge";
import Button from "../../../components/Button";
import { Usuario } from "../../../types/usuario";
import { FilaEntrega } from "../../../services/filaEntregaService";
import { Entrega } from "../../../services/entregaService";
import { Loader2 } from "lucide-react";

interface EntregadorCardProps {
  entregador: Usuario;
  variant?: "iniciar" | "operando";
  filaEntrega?: FilaEntrega;
  entregaAtiva?: Entrega;
  onAction: () => void;
  isActionLoading?: boolean;
  isFinalizando?: boolean;
  actionDisabled?: boolean;
  actionDisabledReason?: string;
  actionText: string;
  actionIcon: React.ReactNode;
  actionButtonClassName?: string;
  showAction?: boolean;
}

const EntregadorCard = ({
  entregador,
  variant = "iniciar",
  filaEntrega,
  entregaAtiva,
  onAction,
  isActionLoading = false,
  isFinalizando = false,
  actionDisabled = false,
  actionDisabledReason,
  actionText,
  actionIcon,
  actionButtonClassName,
  showAction = true,
}: EntregadorCardProps) => {
  const isEmAndamento =
    variant === "operando" &&
    (filaEntrega?.status === "em_andamento" || !!entregaAtiva);
  const statusText =
    variant === "operando"
      ? isEmAndamento
        ? "Em Andamento"
        : "Aguardando"
      : entregador.ativo
        ? "Ativo"
        : "Inativo";

  return (
    <div className="relative rounded-lg border border-neutral-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md sm:p-6">
      {isFinalizando && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-black bg-opacity-50">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-12 w-12 animate-spin text-white" />
            <p className="text-sm font-semibold text-white">Finalizando...</p>
          </div>
        </div>
      )}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-base font-semibold text-neutral-900 sm:text-lg">
            {entregador.nome}
          </h3>
        </div>
        <Badge
          variant={
            variant === "operando"
              ? isEmAndamento
                ? "admin"
                : "user"
              : entregador.ativo
                ? "user"
                : "admin"
          }
        >
          {statusText}
        </Badge>
      </div>
      {entregador.loja && (
        <div className="mt-3 border-t border-neutral-100 pt-3 sm:mt-4 sm:pt-4">
          <p className="text-xs text-neutral-600 sm:text-sm">
            <span className="font-medium">Loja:</span>{" "}
            <span className="break-words">{entregador.loja.nome}</span>
          </p>
          {entregaAtiva && (
            <p className="mt-1 text-xs text-neutral-600 sm:text-sm">
              <span className="font-medium">Endereços:</span>{" "}
              {entregaAtiva.quantidadeEnderecos}
            </p>
          )}
        </div>
      )}
      {showAction && (
        <div className="mt-3 sm:mt-4">
          <Button
            onClick={onAction}
            disabled={isActionLoading || actionDisabled}
            text={actionText}
            icon={actionIcon}
            className={`w-full transform rounded-lg bg-secondary px-4 py-2 text-sm font-bold text-white shadow-lg transition duration-300 hover:scale-105 hover:bg-secondary-dark hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100 sm:py-3 sm:text-base ${actionButtonClassName || ""}`}
          />
          {actionDisabled && actionDisabledReason && (
            <p className="mt-2 text-center text-xs font-medium text-amber-700">
              {actionDisabledReason}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default EntregadorCard;
